// BCM-898 · PA8 — Pre-IBD / Pre-GRN check (slides 23-27). [Shekar]
// Header + "N OF 4 CHECKS FAILED" badge, then ALL FOUR checks as detail cards —
// green (passed) or pink (failed) — each with a per-check Sync button (Sync
// Inventory for inventory checks, Sync PO for PO checks). Matches BOTH mockups:
// the all-failed Pre-IBD slide and the mixed pass/fail Pre-GRN slide.
//
// FIELD SOURCE. checkConsumedBatchExpiry (goodsReceiptApi) —
// workflow/checkForConsumptionBatchExpiry — same endpoint PA4 uses for the
// plain expiry check, but called here in its GATE-VALIDATION mode
// (IBDValidation:true), which returns the authoritative pass/fail for all
// four checks in one response, replacing the old A6+A5+S5-derived computation
// entirely (no fallback — the old reads are removed, not deprioritized):
//   Request:  { schrockenDeliveryScheduleBatchID, scheduleAgreementNumber,
//               schrockenSLID, collaboratorTypeId:1, IBDValidation:true,
//               GRNValidation, skipNotification:true }
//     GRNValidation = true when A2's progressBar.statusSequence contains a
//     "GRN Generated" entry, false when it contains "IBD Generated" instead —
//     same direction isGRNFlow(detail) already computes for the mockup's
//     Pre-IBD/Pre-GRN header wording (the array is the flow's fixed template,
//     so presence of the label matters, not its completed/upcoming state).
//   Response: { status, summary:{total,passed,failed}, hasExpiredBatches,
//               correctStorageLocation, stockAvailable, correctProductPlantSloc,
//               checks:[{type, passed, reason, items?|poStorageLocationInSap
//               etc.}] } — status here reflects "all checks passed", NOT
//               request success, so success is detected from the PRESENCE of
//               checks[], not from status.
// 1 expiredBatch (Sync Inventory)   2 stockAvailability (Sync Inventory)
// 3 storageLocation (Sync PO)   4 productPlantSloc (Sync PO, + MARD button)
// Sync PO / Sync Inventory here trigger recheck + reset. MARD Data Updated / Recheck
// need D6 — the button is shown (per mockup) but reports the pending dependency.
//
// Flow direction (Pre-IBD vs Pre-GRN): IBD requires SLoc 0002 (Quality Hold);
// GRN requires NOT 0002 (Unrestricted) — the API's own requiredStorageLocation
// text already carries this, so the FE no longer restates the rule itself for
// check 3 (only for the header's flow name / GRNValidation flag).
import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Typography, CircularProgress, Box, Button, Snackbar, Paper } from "@material-ui/core";
import SectionShell from "../components/SectionShell";
import FieldCard from "../components/common/FieldCard";
import SyncActions from "../components/common/SyncActions";
import SuccessToast from "../components/common/SuccessToast";
import { checkConsumedBatchExpiry, sendMasterDataUpdateNotification } from "../api/goodsReceiptApi";
import { useGRStyles } from "../goodsReceiptStyles";
import { formatDate, trimQty } from "../utils/formatters";

// A GRN flow requires an unrestricted SLoc; an IBD flow requires 0002. The
// flow is identified from A2's own progressBar.statusSequence — its
// statusName entries are the flow's fixed template (every step the flow will
// ever pass through, regardless of state), so a GRN-generating batch's
// sequence contains a "GRN Generated" entry and an IBD-generating batch's
// contains "IBD Generated" — never both. Same signal drives GRNValidation on
// the checkForConsumptionBatchExpiry request and the Pre-IBD/Pre-GRN header
// wording. Defaults to Pre-IBD (false) when the sequence hasn't loaded yet or
// carries neither label.
const isGRNFlow = (detail) => {
  const seq = (detail && detail.progressBar && detail.progressBar.statusSequence) || [];
  return seq.some((s) => s && s.statusName === "GRN Generated");
};

// ── Per-check-type field builders — one FieldCard per check. ───────────────
// A batch can consume more than one material (the API's `items` array), and
// every one of them needs its own row — previously this picked a single
// "representative" item (the first failing one, or items[0]) and silently
// dropped the rest, so a batch consuming 2 materials only ever showed 1.
// Each item still contributes exactly 3 fields so they keep landing one
// material per row in FieldCard's fixed 3-column grid (goodsReceiptStyles.js
// fieldGrid) regardless of how many items there are — a variable field count
// per item would misalign every row after the first (see the inline shortfall
// note in fieldsForStockAvailability, added for the same reason). Only the
// first item's row carries the column labels (BATCH NUMBER / EXPIRY DATE /
// QTY etc) — repeating them on every material's row read as a stack of
// identical mini-headers, so rows after the first show values only, same as
// a table body under one header row.
const fieldsForExpiredBatch = (c) => {
  const items = Array.isArray(c.items) ? c.items : [];
  if (items.length === 0) return [{ label: "Consumed Material", value: "No consumed material reported." }];
  return items.flatMap((item, idx) => [
    { label: idx === 0 ? "Consumed Material Batch Number" : "", value: item.consumedMaterialBatchNumber },
    {
      label: idx === 0 ? "Expiry Date" : "",
      value: `${formatDate(item.expiryDate)}${item.isExpired && item.expiredDaysAgo != null ? ` (expired ${item.expiredDaysAgo} days ago)` : ""}`,
      red: !!item.isExpired,
    },
    { label: idx === 0 ? "Consumed Material Qty" : "", value: `${trimQty(item.consumedMaterialQuantity)} ${item.consumedMaterialUoM || ""}` },
  ]);
};

const fieldsForStockAvailability = (c) => {
  const items = Array.isArray(c.items) ? c.items : [];
  if (items.length === 0) return [{ label: "Consumed Material", value: "No consumed material reported." }];
  return items.flatMap((item, idx) => [
    { label: idx === 0 ? "Consumed Material Batch Number" : "", value: item.consumedMaterialBatchNumber },
    { label: idx === 0 ? "Consumed Material Qty" : "", value: `${trimQty(item.consumedMaterialQuantity)} ${item.consumedMaterialUoM || ""}` },
    {
      label: idx === 0 ? "Stock Available In Inventory" : "",
      value: `${trimQty(item.stockAvailableInInventory)} ${item.consumedMaterialUoM || ""}${!item.sufficient ? ` (short by ${trimQty(item.shortfall)} ${item.consumedMaterialUoM || ""})` : ""}`,
      red: !item.sufficient,
    },
  ]);
};

const fieldsForStorageLocation = (c) => [
  { label: "PO Storage Location In SAP", value: c.poStorageLocationInSap, red: !c.passed },
  { label: "Required Storage Location", value: c.requiredStorageLocation },
  { label: "PO Number", value: c.poNumber },
];

const fieldsForProductPlantSloc = (c) => {
  const po = c.poProductPlantSloc || {};
  const master = c.masterProductPlantSloc;
  const poValue = `${po.productCode || "-"} / ${po.plant || "-"} / ${po.storageLocation || "-"}`;
  return [
    { label: "PO Product-Plant-SLoc", value: poValue },
    {
      label: "Master Data Product-Plant-SLoc",
      // When the check has passed, mirror the PO's own value exactly — "matches
      // master data" should read as identical on screen, instead of showing the
      // raw masterProductPlantSloc.storageLocation, which can technically satisfy
      // the backend's match rule without being string-identical to the PO's
      // value (e.g. VETT vs INTR). Failed case is completely unchanged — still
      // shows the real master data value (in red) so the mismatch is visible.
      value: c.passed
        ? poValue
        : master
        ? `${master.productCode || po.productCode || "-"} / ${master.plant || "-"} / ${master.storageLocation || "-"}`
        : "Product is missing",
      red: !c.passed,
    },
    { label: "PO Number", value: c.poNumber },
  ];
};

const CHECK_META = {
  expiredBatch: { id: 1, syncType: "INVENTORY", fields: fieldsForExpiredBatch },
  stockAvailability: { id: 2, syncType: "INVENTORY", fields: fieldsForStockAvailability },
  storageLocation: { id: 3, syncType: "PO", fields: fieldsForStorageLocation },
  productPlantSloc: { id: 4, syncType: "PO", fields: fieldsForProductPlantSloc },
};

const MARD_NOTE = "Either change the PO data to match master data and sync using the Sync PO button, or update the MARD SAP table to match the PO data and click MARD Data Updated to confirm the change.";

const buildChecksFromResponse = (data) => {
  const raw = (data && Array.isArray(data.checks)) ? data.checks : [];
  return raw
    .map((c) => {
      const meta = CHECK_META[c.type];
      if (!meta) return null;
      const passed = !!c.passed;
      const isProductPlantSloc = meta.id === 4;
      return {
        id: meta.id,
        passed,
        title: c.reason || "",
        syncType: meta.syncType,
        mard: isProductPlantSloc && !passed,
        fields: meta.fields(c),
        note: isProductPlantSloc && !passed ? MARD_NOTE : undefined,
        // Kept for the MARD Data Updated button — it needs the raw
        // poProductPlantSloc/masterProductPlantSloc/poNumber to build the
        // sendMasterDataUpdateNotification payload, not the display strings.
        raw: c,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.id - b.id);
};

const PA8PreIBDGRNCheck = ({ row, detail, featuresAccess, viewOnly, onRefresh }) => {
  const classes = useGRStyles();
  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mardToast, setMardToast] = useState(false);
  const [mardSending, setMardSending] = useState(false);
  const [mardError, setMardError] = useState("");
  const cancelRef = useRef(null);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (cancelRef.current) cancelRef.current.cancel("leaving PA8 pre-IBD/GRN check");
    };
  }, []);

  useEffect(() => {
    if (!row || !row.schrockenDeliveryScheduleBatchID) return;
    setLoading(true);
    setError("");
    if (cancelRef.current) cancelRef.current.cancel("superseded PA8 fetch");
    const source = axios.CancelToken.source();
    cancelRef.current = source;

    checkConsumedBatchExpiry(
      {
        schrockenDeliveryScheduleBatchID: Number(row.schrockenDeliveryScheduleBatchID),
        scheduleAgreementNumber: row.scheduleAgreementNumber,
        schrockenSLID: row.schrockenSLID,
        collaboratorTypeId: 1,
        IBDValidation: true,
        GRNValidation: isGRNFlow(detail),
      },
      source.token
    )
      .then((res) => {
        if (!mountedRef.current) return;
        const data = res && res.data;
        // status reflects "all checks passed", not request success — success is
        // detected from checks[] being present, not from data.status.
        if (data && Array.isArray(data.checks)) {
          setChecks(buildChecksFromResponse(data));
        } else {
          setError((data && data.message) || "Could not load Pre-IBD/Pre-GRN check data.");
        }
        setLoading(false);
      })
      .catch((err) => {
        if (axios.isCancel(err)) return;
        if (!mountedRef.current) return;
        setError((err.response && err.response.data && err.response.data.message) || err.message);
        setLoading(false);
      });
  }, [row, detail]);

  // MARD Data Updated — no batch processing of its own (slide 26/28): it just
  // fires the confirmation notification to the Collabrix support team so they
  // update MARD in SAP. `check` is the raw productPlantSloc check (check.raw).
  const handleMardDataUpdated = (check) => {
    if (mardSending) return;
    const po = (check && check.poProductPlantSloc) || {};
    const master = check && check.masterProductPlantSloc;
    setMardSending(true);
    setMardError("");
    sendMasterDataUpdateNotification({
      bmsPONumber: (check && check.poNumber) || row.scheduleAgreementNumber,
      cmoName: row.vendorName,
      poProductPlantSLoc: { product: po.productCode, plant: po.plant, storageLocation: po.storageLocation },
      masterDataProductPlantSLoc: master
        ? { product: master.productCode || po.productCode, plant: master.plant, storageLocation: master.storageLocation }
        : null,
    })
      .then((res) => {
        if (!mountedRef.current) return;
        if (res.data && res.data.status) setMardToast(true);
        else setMardError((res.data && res.data.message) || "Failed to send the MARD update notification.");
        setMardSending(false);
      })
      .catch((err) => {
        if (axios.isCancel(err)) return;
        if (!mountedRef.current) return;
        setMardError((err.response && err.response.data && err.response.data.message) || err.message);
        setMardSending(false);
      });
  };

  const grn = isGRNFlow(detail);
  const flowName = grn ? "Pre-GRN" : "Pre-IBD";
  const failedCount = checks.filter((c) => !c.passed).length;
  const allPassed = checks.length > 0 && failedCount === 0;
  // BCM-898 bug fix (reverted): an earlier pass filtered Pre-IBD down to only
  // its failed checks (per an FS quote saying Pre-IBD shows failures only).
  // Live testing after PO sync showed that's wrong in practice — users need
  // every applicable check's status, passed and failed, for BOTH flows — so
  // Pre-IBD now renders the full `checks` list exactly like Pre-GRN always has.

  return (
    <SectionShell
      warn={!allPassed}
      title={allPassed ? `${flowName} Check Passed` : `${flowName} Check Failed`}
      badge={
        checks.length
          ? allPassed
            ? "ALL CHECKS PASSED"
            : `${failedCount} OF ${checks.length} CHECKS FAILED`
          : null
      }
      badgeSuccess={allPassed}
      subText={
        allPassed
          ? `All ${flowName.toLowerCase()} checks have passed. The Generate IBD / GRN button appears once the workflow raises the generation flag.`
          : grn
          ? "Goods receipt cannot be posted for this batch. Resolve the failures below and re-run the Pre-GRN check."
          : "This batch cannot be posted to SAP. Resolve the failures below and re-run the Pre-IBD check."
      }
      viewOnly={viewOnly}
    >
      {loading ? (
        <Box display="flex" justifyContent="center" p={2}><CircularProgress size={22} /></Box>
      ) : error ? (
        <Typography variant="body2" style={{ color: "#C0392B" }}>{error}</Typography>
      ) : checks.length === 0 ? (
        <Typography variant="body2" style={{ color: "#777" }}>
          No check data is available for this batch yet.
        </Typography>
      ) : (
        checks.map((c, i) => (
          <FieldCard
            key={`${c.id}-${i}`}
            passed={c.passed}
            title={`${c.id}. ${c.title}`}
            fields={c.fields}
            note={c.note}
            rightAction={
              <SyncActions
                row={row}
                detail={detail}
                featuresAccess={featuresAccess}
                showSyncInventory={c.syncType === "INVENTORY"}
                showSyncPO={c.syncType === "PO"}
                resetsBatchInventory
                resetsBatchPO
                onRefresh={onRefresh}
              />
            }
            // Lives INSIDE this check's own card, below the note — not a
            // separate block after all four cards — matching the mockup.
            footerAction={
              c.mard ? (
                <Button
                  className={classes.blueBtn}
                  disableElevation
                  disabled={mardSending}
                  onClick={() => handleMardDataUpdated(c.raw)}
                >
                  {mardSending ? "Sending..." : "MARD Data Updated"}
                </Button>
              ) : undefined
            }
          />
        ))
      )}

      <SuccessToast
        open={mardToast}
        message="You have successfully confirmed that MARD table data is updated. Notification has been sent to Collabrix support team to update master data."
        onClose={() => setMardToast(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        banner
      />

      <Snackbar open={!!mardError} autoHideDuration={5000} onClose={() => setMardError("")} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Paper elevation={4} style={{ padding: "12px 18px", borderRadius: 8 }}>
          <Typography style={{ fontSize: "0.85rem", color: "#C0392B" }}>{mardError}</Typography>
        </Paper>
      </Snackbar>
    </SectionShell>
  );
};

export default PA8PreIBDGRNCheck;
